<template>
  <sc-page-base
    :title="title"
    :loading="loading"
  >
    <section v-if="quotation">
      <div class="level">
        <div class="level-item has-text-centered page-quotation__header-item">
          <div>
            <p class="heading">
              <strong>CEP de origem</strong>
            </p>
            <p class="title is-6">{{ quotation.origin_zip_code | cep }}</p>
          </div>
        </div>

        <div class="level-item has-text-centered page-quotation__header-item">
          <div>
            <p class="heading">
              <strong>CEP de destino</strong>
            </p>
            <p class="title is-6">{{ quotation.destination_zip_code | cep }}</p>
          </div>
        </div>

        <div class="level-item has-text-centered page-quotation__header-item">
          <div>
            <p class="heading">
              <strong>Criada em</strong>
            </p>
            <p class="title is-6">{{ quotation.created | date('DD/MM/YYYY HH:mm') }}</p>
          </div>
        </div>

        <div class="level-item has-text-centered page-quotation__header-item">
          <div>
            <p class="heading">
              <strong>Plataforma</strong>
            </p>
            <p class="title is-6">{{ quotation.platform }}</p>
          </div>
        </div>

        <div class="level-item has-text-centered">
          <div>
            <p class="field">
              <a
                :href="`/ng#/create-order/${quotation.id}`"
                class="button is-primary is-fullwidth">
                Criar pedido
              </a>
            </p>
            <p class="field">
                <a href="/ng#/quote/volume/requote/"
                  class="button is-warning is-fullwidth">
                Cotar novamente
              </a>
            </p>
          </div>
        </div>

      </div>

      <div v-if="quotation.order && quotation.order.volumes.length > 0">
        <h2 class="subtitle has-text-weight-bold has-text-primary is-size-4">
          Volumes ({{ quotation.order.volumes.length }})
        </h2>

        <div class="columns">
          <div
            class="column is-4"
            v-for="(volume, volumeKey) in quotation.order.volumes"
            :key="volumeKey">
            <sc-card>
              <template slot="header">
                <h2 class="has-text-primary is-size-6 card-header-title">Volume {{ volumeKey + 1 }}</h2>
              </template>

              <template slot="body">
                <p
                  class="heading"
                  v-if="volume.type">
                  <strong>Tipo de volume:</strong>
                  <span>{{ volume.type.label }}</span>
                </p>

                <p
                  class="heading"
                  v-if="volume.type">
                  <strong>Custo dos produtos:</strong>
                  <span>{{ volume.cost_of_goods | currency }}</span>
                </p>

                <p
                  class="heading"
                  v-if="volume.weight">
                  <strong>Peso:</strong>
                  <span>{{ volume.weight | weight }}</span>
                </p>

                <p
                  class="heading"
                  v-if="volume.height && volume.width && volume.length">
                  <strong>Dimensões:</strong>
                  <span>{{ volume.height }} x {{ volume.width }} x {{ volume.length }} cm</span>
                </p>
              </template>
            </sc-card>
          </div>
        </div>
      </div>

      <hr/>

      <div v-if="quotation.logistic_provider && quotation.logistic_provider.delivery_methods.length > 0">
        <h2 class="subtitle has-text-weight-bold has-text-primary is-size-4">
          Opções de Envio ({{ quotation.logistic_provider.delivery_methods.length }})
        </h2>

        <div class="columns" style="flex-flow: row wrap;">
          <div
            class="column is-4"
            v-for="(deliveryMethod, deliveryMethodKey) in quotation.logistic_provider.delivery_methods"
            :key="deliveryMethodKey">
            <sc-card>
              <template slot="header">
                <sc-tooltip
                  v-if="isRemovedBy(deliveryMethod)"
                  :label="tooltipLabel(deliveryMethod)"
                  type="is-dark"
                  animated>

                <p :class="{'has-text-primary is-size-6 card-header-title has-text-line-through': isRemovedBy(deliveryMethod)}">
                  {{ deliveryMethod.name }}
                </p>
                </sc-tooltip>
                <p v-else class="has-text-primary is-size-6 card-header-title">{{ deliveryMethod.name }}</p>
              </template>

              <template slot="body">
                <div class="columns">
                  <div class="column">
                    <p
                      class="heading"
                      v-if="deliveryMethod.name">
                      <strong>Método de envio:</strong>
                      <span>{{ deliveryMethod.name }}</span>
                    </p>

                    <p
                      class="heading"
                      v-if="deliveryMethod.description">
                      <strong>Nome exibido:</strong>
                      <span>{{ deliveryMethod.description }}</span>
                    </p>

                    <p
                      class="heading"
                      v-if="deliveryMethod.type">
                      <strong>Tipo de Método:</strong>
                      <span>{{ deliveryMethod.type.label }}</span>
                    </p>

                    <p
                      class="heading"
                      v-if="deliveryMethod.final_shipping_cost">
                      <strong>Frete exibido:</strong>
                      <span>{{ deliveryMethod.final_shipping_cost | currency }}</span>
                    </p>

                    <p
                      class="heading"
                      v-if="deliveryMethod.delivery_estimate_minutes || deliveryMethod.delivery_estimate_business_days">
                      <strong>Prazo de entrega:</strong>
                      <span>{{ deliveryMethod.delivery_estimate_minutes ? `${deliveryMethod.delivery_estimate_minutes} minuto(s)` : `${deliveryMethod.delivery_estimate_business_days} dia(s)` }}</span>
                    </p>

                    <p
                      class="heading" v-if="deliveryMethod.delivery_estimate_date_minutes_iso">
                      <strong>Data prevista da entrega:</strong>
                      <span>{{ Moment(deliveryMethod.delivery_estimate_date_minutes_iso).format('L') +' ÀS '+Moment(deliveryMethod.delivery_estimate_date_minutes_iso).format('LT')}}</span>
                    </p>

                    <p
                      class="heading"
                      v-if="deliveryMethod.cost">
                      <strong>Custo de envio:</strong>
                      <span>{{ deliveryMethod.cost | currency }}</span>
                    </p>
                  </div>
                  <div class="column is-narrow">
                    <figure class="image is-128x128">
                      <img
                        :src="deliveryMethod.logo_url"
                        :alt="deliveryMethod.name" />
                    </figure>

                    <a
                      @click="openQuotingDetailRecote(quotation, deliveryMethod, backUrl)"
                      class="button is-primary" target="_blank">
                        Cotação Detalhada
                    </a>
                  </div>
                </div>
              </template>
            </sc-card>
          </div>
        </div>
      </div>

      <hr/>

      <div v-if="quotation.delivery_options_debug && quotation.delivery_options_debug.length > 0">
          <h2 class="subtitle has-text-weight-bold has-text-primary is-size-4">
            Opções de Envio Não Disponíveis ({{ quotation.delivery_options_debug.length }})
          </h2>
          <div class="columns" style="flex-flow: row wrap;">
            <div
               class="column is-4"
               v-for="(excludedMethod, excludedMethodKey) in quotation.delivery_options_debug"
               :key="excludedMethodKey"
              >
              <sc-card >
                <template slot="header">
                    <p class="has-text-primary is-size-6 card-header-title">
                      {{ excludedMethod.name }}
                    </p>
                </template>
                <template slot="body">
                  <div class="columns">
                    <div class="column">
                      <p
                        v-for="(item, index) in excludedMethod.restriction_messages"
                        :key="index"
                        class="heading">
                          {{ item }}
                      </p>

                      <p
                        class="heading"
                        v-if="excludedMethod.name">
                        <strong>Método de envio:</strong>
                        <span>{{ excludedMethod.name }}</span>
                      </p>
                      <p
                        class="heading"
                        v-if="excludedMethod.delivery_method_type">
                        <strong>Tipo de Método:</strong>
                        <span>{{ excludedMethod.delivery_method_type.label }}</span>
                      </p>
                      <p
                        class="heading"
                        v-if="excludedMethod.final_shipping_cost">
                        <strong>Frete exibido:</strong>
                        <span>{{ excludedMethod.final_shipping_cost | currency }}</span>
                      </p>

                      <p
                        class="heading"
                        v-if="excludedMethod.delivery_estimate_business_days || excludedMethod.freight.delivery_estimate_minutes">
                        <strong>Prazo de entrega:</strong>
                        <span>{{ deliveryTimeDescrption(excludedMethod) }}</span>
                      </p>
                      <p
                        class="heading" v-if="excludedMethod.freight.delivery_estimate_date_minutes_iso">
                        <strong>Data prevista da entrega:</strong>
                        <span>{{ Moment(excludedMethod.freight.delivery_estimate_date_minutes_iso).format('L') +' ÀS '+Moment(excludedMethod.freight.delivery_estimate_date_minutes_iso).format('LT')}}</span>
                      </p>

                      <p
                        class="heading"
                        v-if="excludedMethod.cost">
                        <strong>Custo de envio:</strong>
                        <span>{{ excludedMethod.cost | currency }}</span>
                      </p>
                    </div>
                  </div>
                </template>
              </sc-card>
            </div>
          </div>
      </div>
    </section>
  </sc-page-base>
</template>

<script>
  import {
    ScPageBase,
    ScCard,
    ScButton,
    ScTooltip,
  } from '@intelipost-js/secure-components';
  import { createNamespacedHelpers } from 'vuex';
  import { Moment } from '@intelipost-js/secure-providers';

  const {
    mapState,
    mapActions,
  } = createNamespacedHelpers('quote/quotations');

  export default {
    name: 'page-quotation',
    data() {
      return {
        Moment,
        backUrl: window.location.href,
        params: {},
        id: null,
      };
    },
    computed: {
      title() {
        const { id } = this.quotation || {};
        const title = 'Detalhe da Cotação';
        return id ? `${title} (${id})` : title;
      },
      ...mapState([
        'loading',
        'quotation',
      ]),
    },
    methods: {
      ...mapActions([
        'getQuotation',
        'getIsValidDeliveryMethodRecote',
      ]),
      deliveryTimeDescrption(methodDelivery) {
        if (methodDelivery.freight.delivery_estimate_minutes) {
          return `${methodDelivery.freight.delivery_estimate_minutes} minuto(s)`;
        }
        return `${methodDelivery.final_delivery_time || methodDelivery.delivery_estimate_business_days} dia(s)`;
      },
      openQuotingDetailRecote(quotation, deliveryMethod, backUrl) {
        this.getQuotation({ id: this.id, show_error: true })
          .then((data) => {
            if (!data) {
              this.redirectToDetailPage(quotation, backUrl, deliveryMethod);
            } else if (this.checkDetailExistisMessage(data)) {
              this.getIsValidDeliveryMethodRecote(
                { id: this.id,
                  idDeliveryMethod: deliveryMethod.id,
                }).then((isValid) => {
                  if (isValid) {
                    this.redirectToDetailPage(quotation, deliveryMethod, backUrl, deliveryMethod);
                  }
                });
            }
          });
      },
      redirectToDetailPage(quotation, backUrl, deliveryMethod) {
        window.location = `/ng#/recote/${quotation.id}/${deliveryMethod.id}?url=${backUrl}&reverse=${deliveryMethod.type.value === 'RETURN'}`;
      },
      checkDetailExistisMessage(data) {
        const ERROR_MESSAGE_QUOTATION_NOT_FOUND = 'Não foi possível encontrar a cotação';
        return data.graphQLErrors &&
                        data.graphQLErrors[0].message.startsWith(ERROR_MESSAGE_QUOTATION_NOT_FOUND);
      },
      goToQuotations() {
        return this.$router.push({
          name: 'quotations',
        });
      },
      isRemovedBy(item) {
        return (item.removed_by_quote_rules || item.removed_by_return_modes);
      },
      tooltipLabel(item) {
        if (item.removed_by_quote_rules) {
          return 'Excluído por Regra de Cotação';
        }

        if (item.removed_by_return_modes) {
          return 'Excluído por Método de Retorno';
        }

        return null;
      },
    },
    created() {
      const {
        params = {},
      } = this.$route;
      const {
        id = null,
      } = params || {};

      this.id = id;

      if (id) {
        return this.getQuotation({ id, show_error: false })
          .catch(() => this.goToQuotations());
      }

      return this.goToQuotations();
    },
    components: {
      ScPageBase,
      ScCard,
      ScButton,
      ScTooltip,
    },
  };
</script>

<style lang="scss" scoped>
  .page-quotation{
    &__header{
      &-item{
        background-color: #fff;
        border-radius: 20px;
        border: 1px solid #dbdbdb;
        margin: 0 .5rem;min-height: 70px
      }
    }
  }
</style>



// WEBPACK FOOTER //
// node_modules/@intelipost-modules/secure-quote/features/quotations/pages/quotation/PageQuotation.vue?1e8a2ce6